<i18n lang="yaml">
en:
  default: 'Nearby places to {name}'
  placeType:
    City: 'Cities nearby {city}, {region}'
    Region: 'Cities across {region}, {country}'
    Country: 'Regions within {country}'
  regionsType:
    Region: Regions
    State: States
    Province: Provinces
    District: Districts
    Territory: Territories
fr:
  default: 'Endroits à proximité de {name}'
  placeType:
    City: 'Villes à proximité de {city}, {region}'
    Region: 'Villes à travers {region}, {country}'
    Country: 'Régions dans {country}'
  regionsType:
    Region: Régions
    State: États
    Province: Provinces
    District: Districts
    Territory: Territoires
</i18n>

<template>
  <Container class="mt-8 mb-8">
    <h2 class="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">{{ title }}</h2>

    <dl
      class="mx-auto mt-8 grid max-w-2xl grid-cols-1 gap-x-8 gap-y-4 text-base leading-7 sm:grid-cols-2 lg:mx-0 lg:max-w-none lg:grid-cols-4"
    >
      <PlaceNearbyPlaceItem
        v-for="nearbyPlace in nearbyPlaces"
        :key="nearbyPlace.id"
        :place="nearbyPlace"
        :unit="distanceUnit"
        :use-full-name="useFullName"
      />
    </dl>
  </Container>
</template>

<script lang="ts" setup>
import type { Place } from '~/types/api'

const props = defineProps<{
  place: Place
  nearbyPlaces: Place[]
}>()

const { t } = useI18n()

const distanceUnit = computed(() => getDistanceUnit(props.place.countryCode ?? 'XX'))

const title = computed(() => {
  switch (props.place.placeType) {
    case 'City':
      return t('placeType.City', { city: props.place.name, region: props.place.regionCode })

    case 'Region':
      return t('placeType.Region', { region: props.place.name, country: props.place.countryCode })

    case 'Country':
      return t('placeType.Country', { country: props.place.name })

    default:
      return t('default', { name: props.place.name })
  }
})

const useFullName = computed(() => props.place.placeType !== 'Country')
</script>
